import { useState, useRef, useCallback } from 'react'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'

const API_BASE = import.meta.env.VITE_API_URL || ''

/** Prepend VITE_API_URL to relative /api/ paths so thumbnails load in production. */
function resolveUrl(url: string): string {
  return url.startsWith('/api/') ? `${API_BASE}${url}` : url
}

export interface FileUploadProps {
  value?: string[]
  onChange: (urls: string[]) => void
  maxFiles?: number
  accept?: string
  label?: string
  hint?: string
  disabled?: boolean
  onUpload?: (file: File) => Promise<string>
}

export function FileUpload({
  value = [],
  onChange,
  maxFiles = 5,
  accept = 'image/*',
  label,
  hint,
  disabled = false,
  onUpload,
}: FileUploadProps) {
  const { t } = useTranslation('common')
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [, setUploadProgress] = useState<Record<string, number>>({})
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return
      if (value.length >= maxFiles) return

      const filesToUpload = Array.from(files).slice(0, maxFiles - value.length)

      if (onUpload) {
        setIsUploading(true)
        const newUrls: string[] = []

        for (const file of filesToUpload) {
          try {
            setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }))
            const url = await onUpload(file)
            newUrls.push(url)
            setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }))
          } catch (error) {
            console.error('Upload failed:', error)
          }
        }

        onChange([...value, ...newUrls])
        setIsUploading(false)
        setUploadProgress({})
      } else {
        // For demo/local preview - convert to base64
        const promises = filesToUpload.map(
          (file) =>
            new Promise<string>((resolve) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result as string)
              reader.readAsDataURL(file)
            })
        )
        const base64Urls = await Promise.all(promises)
        onChange([...value, ...base64Urls])
      }
    },
    [value, maxFiles, onChange, onUpload]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      if (!disabled) {
        handleFiles(e.dataTransfer.files)
      }
    },
    [disabled, handleFiles]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const removeFile = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const canAddMore = value.length < maxFiles

  return (
    <div className="space-y-3">
      {label && <label className="label-text font-medium">{label}</label>}

      {/* Preview Grid */}
      {value.length > 0 && (
        <div className={`grid gap-3 ${value.length === 1 ? 'grid-cols-1 max-w-48 mx-auto' : 'grid-cols-2 md:grid-cols-4'}`}>
          {value.map((url, index) => (
            <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-base-200">
              <img src={resolveUrl(url)} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
              <Button
                type="button"
                variant="error"
                size="xs"
                shape="circle"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeFile(index)}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {canAddMore && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : disabled
                ? 'border-base-300 bg-base-200 cursor-not-allowed'
                : 'border-base-300 hover:border-primary cursor-pointer'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={maxFiles > 1}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            disabled={disabled}
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="text-sm text-base-content/60">{t('file_upload.uploading')}</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center">
                {accept.includes('image') ? (
                  <ImageIcon className="w-6 h-6 text-base-content/60" />
                ) : (
                  <Upload className="w-6 h-6 text-base-content/60" />
                )}
              </div>
              <div>
                <span className="text-primary font-medium">{t('file_upload.click_to_upload')}</span>
                <span className="text-base-content/60"> {t('file_upload.or_drag_drop')}</span>
              </div>
              {hint && <p className="text-xs text-base-content/60">{hint}</p>}
              <p className="text-xs text-base-content/40">
                {t('file_upload.files_counter', { count: value.length, max: maxFiles })}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
